import React from "react";
import { CircularProgressbar, buildStyles } from "react-circular-progressbar";
import { PieChart, Pie, Cell } from "recharts";
import "react-circular-progressbar/dist/styles.css";

// Portfolio data structure for risk exposure
const portfolioData = [
  { name: "Cash & Equivalents", percentage: 20, color: "#00FF00" },
  { name: "CDs/Fixed Annuities", percentage: 15, color: "#ADFF2F" },
  { name: "Low-Risk Bonds", percentage: 20, color: "#FFFF00" },
  { name: "ETFs/Mutual Funds", percentage: 10, color: "#FFA500" },
  { name: "Equities (Blue-Chip)", percentage: 15, color: "#FF7F50" },
  { name: "High-Yield Debt", percentage: 10, color: "#FF4500" },
  { name: "Structured Notes", percentage: 5, color: "#FF6347" },
  { name: "Growth Equities", percentage: 3, color: "#FF0000" },
  { name: "Private Equity", percentage: 1, color: "#8B0000" },
  { name: "Cryptocurrency", percentage: 1, color: "#800080" },
  { name: "Alternative Investments", percentage: 0.5, color: "#6A0DAD" },
  { name: "Speculative Investments", percentage: 0.5, color: "#4B0082" },
];

export const getHealthScoreColor = (score) => {
  if (score >= 95) return { color: "#008000", label: "Excellent" };
  if (score >= 85) return { color: "#32CD32", label: "Very Good" };
  if (score >= 75) return { color: "#90EE90", label: "Good" };
  if (score >= 70) return { color: "#ADFF2F", label: "Acceptable" };
  if (score >= 60) return { color: "#FFD700", label: "Fair" };
  if (score >= 45) return { color: "#FF8C42", label: "Weak" };
  return { color: "#FF4C4C", label: "Critical" };
};

export const HealthScore = ({ score }) => {
  const { color, label } = getHealthScoreColor(score);

  return (
    <div className="flex items-center w-[140px]">
      <div className="w-[40px] h-[40px] relative flex-shrink-0">
        <CircularProgressbar
          value={score}
          text={`${score}`}
          styles={buildStyles({
            textSize: "32px",
            pathColor: color,
            textColor: "currentColor",
            trailColor: "#e5e7eb",
            pathTransitionDuration: 0.5,
          })}
        />
      </div>
      <div className="ml-2 flex flex-col min-w-0 pr-1">
        <div className="text-sm font-medium truncate text-gray-900 dark:text-gray-100">
          {label}
        </div>
        <div className="text-xs text-gray-500 dark:text-gray-400">Health Score</div>
      </div>
    </div>
  );
};

export const RiskExposureChart = ({ riskScore }) => {
  const generatePortfolioData = (score) => {
    let data = [...portfolioData];
    if (score < 50) {
      data[0].percentage = 40;
      data[1].percentage = 30;
      data[2].percentage = 20;
      data[3].percentage = 10;
      data.slice(4).forEach((item) => (item.percentage = 0));
    } else if (score < 70) {
      data[0].percentage = 20;
      data[1].percentage = 20;
      data[2].percentage = 20;
      data[3].percentage = 20;
      data[4].percentage = 15;
      data[5].percentage = 5;
      data.slice(6).forEach((item) => (item.percentage = 0));
    } else {
      data = data.map((item) => ({ ...item, percentage: 8.33 }));
    }
    return data;
  };

  const chartData = generatePortfolioData(riskScore);
  const COLORS = chartData.map((data) => data.color);

  return (
    <div className="flex items-center w-[140px]">
      <div className="w-[40px] h-[40px] relative flex-shrink-0">
        <div
          className="absolute inset-0"
          style={{ transform: "translate(-4px, -4px)" }}
        >
          <PieChart width={48} height={48}>
            <Pie
              data={chartData}
              cx={19}
              cy={19}
              innerRadius={14}
              outerRadius={22}
              dataKey="percentage"
              startAngle={90}
              endAngle={-270}
            >
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[index]} />
              ))}
            </Pie>
          </PieChart>
        </div>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">{riskScore}</span>
        </div>
      </div>
      <div className="ml-2 flex flex-col min-w-0 pr-1">
        <div className="text-sm font-medium truncate text-gray-900 dark:text-gray-100">
          {riskScore >= 75
            ? "Aggressive"
            : riskScore >= 50
              ? "Moderate"
              : "Conservative"}
        </div>
        <div className="text-xs text-gray-500 dark:text-gray-400">Risk Profile</div>
      </div>
    </div>
  );
};

export const generateRiskScore = (answers) => {
  // Implementation of risk score generation based on answers
  return Math.floor(Math.random() * 100); // Placeholder implementation
};
